import {
  StatusCategory,
  SupportClientBillingStanding,
  SupportNotificationKind,
  TicketPriority,
} from '../../graphql/enums.graphql';
import { formatMoney } from '../../utils/formatMoney';

/** Badge indicator per status category; closed gets none (neutral). */
export function categoryIndicator(
  category: StatusCategory,
): 'blue' | 'orange' | 'green' | undefined {
  switch (category) {
    case StatusCategory.Open:
      return 'blue';
    case StatusCategory.Pending:
      return 'orange';
    case StatusCategory.InProgress:
      return 'green';
    default:
      return undefined;
  }
}

export const CATEGORY_LABEL: Record<StatusCategory, string> = {
  [StatusCategory.Open]: 'Open',
  [StatusCategory.Pending]: 'Pending',
  [StatusCategory.InProgress]: 'In progress',
  [StatusCategory.Closed]: 'Closed',
};

export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  [TicketPriority.Low]: 'Low',
  [TicketPriority.Normal]: 'Normal',
  [TicketPriority.High]: 'High',
  [TicketPriority.Urgent]: 'Urgent',
};

/** Only the priorities worth a badge in a list; Normal and Low stay quiet. */
export function priorityIndicator(priority: TicketPriority): 'red' | 'orange' | undefined {
  if (priority === TicketPriority.Urgent) return 'red';
  if (priority === TicketPriority.High) return 'orange';
  return undefined;
}

export const NOTIFICATION_LABEL: Record<SupportNotificationKind, string> = {
  [SupportNotificationKind.TicketCreated]: 'New ticket',
  [SupportNotificationKind.AssignedToYou]: 'Assigned to you',
  [SupportNotificationKind.ReplyOnYourTicket]: 'New reply',
  [SupportNotificationKind.StatusChangedOnYourTicket]: 'Status changed',
  [SupportNotificationKind.Mentioned]: 'Mentioned you',
};

/** "AN" for "Ana", "PS" for "Priya Shah", "–" for nobody. */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '–';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/**
 * Short relative time for lists: "just now", "4 min", "3 h", "Yesterday",
 * "2 d", then a date. `now` is injectable for tests.
 */
export function relativeTime(value: string | Date, now: Date = new Date()): string {
  const at = typeof value === 'string' ? new Date(value) : value;
  const seconds = Math.max(0, Math.round((now.getTime() - at.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} d`;
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** "1 h 12 min", "3 d 4 h", "45 s" — for metric durations given in seconds. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`;
}

/** Bytes as "820 KB" / "4.2 MB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The ticket screen's path for a ticket number. */
export function ticketPath(number: string): string {
  return `/support/tickets/${encodeURIComponent(number)}`;
}

/** What the billing standing badge says. Billing standing only, not a health score. */
export const BILLING_STANDING_LABEL: Record<SupportClientBillingStanding, string> = {
  [SupportClientBillingStanding.Good]: 'Good standing',
  [SupportClientBillingStanding.Overdue]: 'Overdue invoices',
  [SupportClientBillingStanding.NoActiveSubscription]: 'No active subscription',
};

/** Red only for money that is owed; the other two standings stay neutral. */
export function billingStandingBadgeType(
  standing: SupportClientBillingStanding,
): 'destructive' | 'outline' {
  return standing === SupportClientBillingStanding.Overdue ? 'destructive' : 'outline';
}

/**
 * Base plan MRR per currency, never converted: "$100.00 / mo · €50.00 / mo".
 * An empty list (nothing earning revenue now) reads as a dash.
 */
export function formatBaseMrr(mrr: { amount: number; currency: string }[]): string {
  if (mrr.length === 0) return '—';
  return mrr.map((m) => `${formatMoney(m.amount, m.currency)} / mo`).join(' · ');
}
